import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { Checkin } from 'store/modules/checkins'
import { CheckinPerformanceQuestion } from 'store/modules/checkin-performance-questions'
import { HtmlContent } from 'components/atoms/editor/html-content'

export type PerformanceQuestionsSectionProps = {
  checkin: Checkin
}

const NoAnswerYet = () => (
  <span className="text-gray-500 text-sm italic">No answer yet</span>
)

// Open-dialogue answers render as text; rating-scale answers render as a
// coloured badge resolved from the check-in's rating scale.
const AnswerDisplay = ({
  question,
}: {
  question: CheckinPerformanceQuestion
}) => {
  if (question.questionType === 'rating_scale') {
    if (!question.answerLabel) return <NoAnswerYet />

    return (
      <span
        className="self-start rounded px-2 py-1 text-sm font-medium text-white"
        style={{ backgroundColor: question.answerLabel.color }}
      >
        {question.answerLabel.labelName}
      </span>
    )
  }

  if (!question.answerText) return <NoAnswerYet />

  return (
    <p className="m-0 whitespace-pre-line text-gray-900 text-sm">
      {question.answerText}
    </p>
  )
}

// Read-only list of the employee's performance-question answers, shown to the
// manager/assessor on the check-in summary page. Open-dialogue answers render as
// text; rating-scale answers render as a coloured badge resolved from the
// check-in's rating scale.
export const PerformanceQuestionsSection: React.VFC<PerformanceQuestionsSectionProps> =
  observer((props) => {
    const { checkin } = props
    const questions = checkin.checkinPerformanceQuestions

    if (questions.length === 0) return null

    return (
      <div className="flex flex-col gap-3 w-full" data-recording-sensitive>
        <h3 className="font-bold mb-2 text-gray-900 text-xl">
          Performance questions
        </h3>
        <div className="flex flex-col gap-6">
          {questions.map((question) => (
            <div key={question.id} className="flex flex-col gap-1.5">
              <HtmlContent className="font-bold text-gray-900 text-base [&_p]:m-0">
                {question.body}
              </HtmlContent>

              <AnswerDisplay question={question} />
            </div>
          ))}
        </div>
      </div>
    )
  })

PerformanceQuestionsSection.displayName = 'PerformanceQuestionsSection'
